import React, { lazy, Suspense, useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
  useParams,
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  SUPPORTED_LOCALES,
  PROFITROLL_URL,
  JOBALTO_URL,
  PROFITROLL_URL_LANGUAGES,
  IS_SNAP,
  IS_BOT
} from 'variables/main';
import getLocale, { getLocalizedPath } from 'helpers/getLocale';
import { localeToFlagAlpha2, countryToLocale } from 'helpers/flags';
import { API_URL } from 'services/api';
import Preloader from 'components/Preloader';
import ScrollResetOnNavigate from 'components/ScrollResetOnNavigate';
// Eagerly imported assessment skeleton — used as Suspense fallback so users
// never see the prior page (or homepage) while the lazy assessment chunk loads.
import AssessmentShell from 'pages/Assessment/Shell';
const SnackbarWrapper = lazy(() => import('components/Snackbars'));
const CookieConsentCreative = lazy(() => import('components/CookieConsentCreative'));
const Home = lazy(() => import('pages/Home'));
const Professions = lazy(() => import('pages/Professions'));
const ProfessionsAll = lazy(() => import('pages/ProfessionsAll'));
const Profession = lazy(() => import('pages/Profession'));
const Skills = lazy(() => import('pages/Skills'));
const SkillDetail = lazy(() => import('pages/Skills/SkillDetail'));
const SignInScreen = lazy(() => import('pages/SignIn'));
const SignUpScreen = lazy(() => import('pages/SignUp'));
const ResetPassword = lazy(() => import('pages/ResetPassword'));
const Profile = lazy(() => import('pages/Profile'));
const Posts = lazy(() => import('pages/Posts'));
const Post = lazy(() => import('pages/Post'));
const Policy = lazy(() => import('pages/Policy'));
const Company = lazy(() => import('pages/Company'));
const Consultants = lazy(() => import('pages/Consultants'));
const Consultant = lazy(() => import('pages/Consultant'));
const Mentors = lazy(() => import('pages/Mentors'));
const Mentor = lazy(() => import('pages/Mentor'));
const RefundPolicy = lazy(() => import('pages/Policy/Refund'));
const PrivacyPolicy = lazy(() => import('pages/Policy/Privacy'));
const CookiePolicy = lazy(() => import('pages/Policy/Cookie'));
const Order = lazy(() => import('pages/Order'));
const Orders = lazy(() => import('pages/Orders'));
const Page404 = lazy(() => import('pages/404'));
const SubscribeType = lazy(() => import('pages/SubscribeType'));
const UniversityPage = lazy(() => import('pages/University'));
const InstitutionPage = lazy(() => import('pages/Institution'));
const InstitutionsPage = lazy(() => import('pages/Institutions'));
const AboutPage = lazy(() => import('pages/AboutUs'));
const Programs = lazy(() => import('pages/Programs'));
const Vacancies = lazy(() => import('pages/Vacancies'));
const EducationProgramsPage = lazy(() => import('pages/EducationPrograms'));
const EducationPrograms = lazy(() => import('pages/EducationProgramsKlekit'));
const EducationProgram = lazy(() => import('pages/EducationProgram'));
const Sandbox = lazy(() => import('pages/Sandbox'));
const SimulatorHub = lazy(() => import('pages/Simulator'));
const SimulatorPlay = lazy(() => import('pages/Simulator/Play'));
const Vacancy = lazy(() => import('pages/Vacancy'));
const Courses = lazy(() => import('pages/Courses'));
const Course = lazy(() => import('pages/Course'));
const Platforms = lazy(() => import('pages/Platforms'));
const Platform = lazy(() => import('pages/Platform'));
const LocalizationsPage = lazy(() => import('pages/Localizations'));
const AssessmentPage = lazy(() => import('pages/Assessment'));
const AssessmentResultsPage = lazy(() => import('pages/Assessment/Results'));
const AssessmentLandingPage = lazy(() => import('pages/AssessmentLanding'));
const OnboardingPage = lazy(() => import('pages/Onboarding/OnboardingPage'));
const CounselorDashboard = lazy(() => import('pages/Counselor'));
const AssessmentAdminDashboard = lazy(() => import('pages/AssessmentAdmin'));
const CounselorVouchers = lazy(() => import('pages/Counselor/Vouchers'));
const CounselorBuy = lazy(() => import('pages/Counselor/Buy'));
const CounselorBuyReturn = lazy(() => import('pages/Counselor/BuyReturn'));
const CounselorResults = lazy(() => import('pages/Counselor/Results'));
const CounselorListing = lazy(() => import('pages/Counselor/Listing'));
const ProStart = lazy(() => import('pages/Counselor/ProStart'));
const DashboardResolver = lazy(() => import('pages/Dashboard'));
const AuthAction = lazy(() => import('pages/AuthAction'));

const AssessmentRoute = () => (
  <Suspense fallback={<AssessmentShell />}>
    <AssessmentPage />
  </Suspense>
);

const AssessmentResultsRoute = () => (
  <Suspense fallback={<AssessmentShell />}>
    <AssessmentResultsPage />
  </Suspense>
);

// The consultants section was renamed /consultants → /counselors. Legacy URLs
// redirect to their /counselors equivalent, preserving slug/id/page params.
const ConsultantsRedirect = () => {
  const { slug, id, num } = useParams();
  let target = '/counselors';
  if (slug && id) target = `/counselors/${slug}/${id}`;
  else if (num) target = `/counselors/page/${num}`;
  return <Navigate to={getLocalizedPath(target)} replace />;
};

// /profile was renamed to /dashboard (a role-resolver).
const ProfileRedirect = () => <Navigate to={getLocalizedPath('/dashboard')} replace />;

// Counselor area restructured: the dashboard moved to /dashboard, the sub-pages
// to /pro/*. Map every legacy /counselor[/...] URL to its new home.
const CounselorRedirect = () => {
  const location = useLocation();
  const m = location.pathname.match(/^\/[a-z]{2}-[a-z]{2}(\/.*)$/i);
  const rest = m ? m[1] : location.pathname; // strip an optional locale prefix
  const target =
    rest === '/counselor' || rest === '/counselor/profile'
      ? '/dashboard'
      : rest.replace(/^\/counselor/, '/pro');
  return <Navigate to={getLocalizedPath(target)} replace />;
};

const LayoutWithLocale = () => {
  const { lc } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [locale, country] = String(lc || '').split('-');

  // Check if we're on jobalto.com - only apply locale validation there
  // On profitroll.com.ua, URLs don't have locale prefixes, so LayoutDefaultLocale handles them
  const isOnJobalto = typeof window !== 'undefined' &&
    (window.location.hostname === JOBALTO_URL || window.location.hostname === `www.${JOBALTO_URL}`);

  // For everyone that won't be redirected (bots, react-snap, profitroll
  // visitors), render Page404 instead of Outlet when the first path segment
  // isn't a real locale. Without this guard the `/:lc/*` route swallows ANY
  // single-segment URL on profitroll.com.ua (e.g. /zxcvbn → lc='zxcvbn',
  // trailing '*' = '', index route mounts Home), so the SPA renders the home
  // page with a canonical pointing at the unknown URL. Crawlers then index
  // every speculative path they try as a duplicate of /. The check works on
  // jobalto too: bot requests to /xx-yy/... where xx-yy isn't supported now
  // 404 instead of silently serving the home hero. Humans on jobalto keep
  // their existing redirect-to-/en-int behaviour via the block below.
  const isValidLocale = SUPPORTED_LOCALES.includes(locale);
  if (!isValidLocale && !(isOnJobalto && !IS_SNAP && !IS_BOT)) {
    return <Page404 />;
  }

  // Skip all redirects during react-snap, for bots, or when not on jobalto.com
  if (!IS_SNAP && !IS_BOT && isOnJobalto) {
    if (PROFITROLL_URL_LANGUAGES.includes(locale)) {
      // Cross-brand hop — full reload required.
      window.location.href = PROFITROLL_URL;
      return <Preloader />;
    }

    if (!SUPPORTED_LOCALES.includes(locale)) {
      // Same-origin locale correction — stay in SPA.
      const pathSuffix = lc ? `/${lc}` : '';
      const remainingPath = location.pathname.replace(`/${lc}`, '') || '';
      navigate(`/en-int${pathSuffix}${remainingPath}`, { replace: true });
      return <Preloader />;
    }

    const mappedCountry = localeToFlagAlpha2(locale);
    // Allow 'int' (international) as a special 3-letter country code, otherwise require 2-letter code
    if (!country || (!/^[a-z]{2}$/i.test(country) && country.toLowerCase() !== 'int')) {
      // Same-origin locale+country suffix — stay in SPA.
      const pathAfterLocale = location.pathname.replace(`/${lc}`, '').replace(/^\//, '');
      const pathSuffix = pathAfterLocale ? `/${pathAfterLocale}` : '';
      navigate(`/${locale}-${mappedCountry}${pathSuffix}`, { replace: true });
      return <Preloader />;
    }
  }

  return <Outlet />;
};

/**
 * Layout wrapper for non-localized routes (profitroll.com.ua)
 * Handles geo-redirect logic and renders child routes via Outlet
 */
const LayoutDefaultLocale = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [ipCountryChecked, setIpCountryChecked] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Initialize these in state to avoid hydration mismatch - will be set correctly in useEffect
  const [isOnJobalto, setIsOnJobalto] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const currentLocale = getLocale();
  const currentCountry = localeToFlagAlpha2(currentLocale);

  // Detect domain after hydration to avoid SSR/client mismatch
  useEffect(() => {
    const hostname = window.location.hostname;
    setIsOnJobalto(hostname === JOBALTO_URL || hostname === `www.${JOBALTO_URL}`);
    setIsHydrated(true);
  }, []);

  // Check if URL has locale path
  const hasLocaleInPath = location.pathname.match(/^\/[a-z]{2}-[a-z]{2}/);

  // Fetch country from IP and redirect if needed
  useEffect(() => {
    // Skip geo-redirect for react-snap, bots, or if already checked/redirecting
    // Also wait for hydration to complete to avoid hydration mismatches
    if (IS_SNAP || IS_BOT || isRedirecting || ipCountryChecked || !isHydrated) return;

    // Skip IP check for /language-selection - users need to access this page to choose their language
    if (location.pathname === '/language-selection') {
      setIpCountryChecked(true);
      return;
    }

    const fetchCountryAndRedirect = async () => {
      try {
        // If on jobalto.com and already has locale, don't fetch IP
        if (isOnJobalto && hasLocaleInPath) {
          setIpCountryChecked(true);
          return;
        }

        // Fetch country from IP
        const response = await fetch(`${API_URL}location/country-by-ip`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          setIpCountryChecked(true);
          return;
        }

        const data = await response.json();
        const countryCode = data?.data?.countryCode;

        if (!countryCode) {
          setIpCountryChecked(true);
          return;
        }

        // Determine locale from country code
        const detectedLocale = countryToLocale(countryCode);
        const detectedCountry = localeToFlagAlpha2(detectedLocale);

        // If on jobalto.com without locale path, redirect to IP-detected locale.
        // Same-origin → stay in SPA to keep fetch cache / React tree alive.
        if (isOnJobalto && !hasLocaleInPath) {
          setIsRedirecting(true);
          const pathSuffix = location.pathname === '/' ? '' : location.pathname;
          navigate(`/${detectedLocale}-${detectedCountry}${pathSuffix}`, { replace: true });
          return;
        }

        // If on profitroll.com.ua without locale path and detected locale should be on jobalto.com
        // Exclude /language-selection from redirect as users need to access it to choose their language
        if (
          !isOnJobalto &&
          !hasLocaleInPath &&
          location.pathname !== '/language-selection' &&
          !PROFITROLL_URL_LANGUAGES.includes(detectedLocale) &&
          SUPPORTED_LOCALES.includes(detectedLocale)
        ) {
          setIsRedirecting(true);
          // Cross-brand hop — full reload required.
          const pathSuffix = location.pathname === '/' ? '' : location.pathname;
          window.location.href = `https://${JOBALTO_URL}/${detectedLocale}-${detectedCountry}${pathSuffix}`;
          return;
        }

        setIpCountryChecked(true);
      } catch (error) {
        console.error('Error fetching country from IP:', error);
        setIpCountryChecked(true);
      }
    };

    fetchCountryAndRedirect();
  }, [location.pathname, isRedirecting, ipCountryChecked, isOnJobalto, hasLocaleInPath, isHydrated, navigate]);

  // Don't make any redirect decisions until hydration is complete
  // This prevents hydration mismatches between SSR and client
  if (isHydrated) {
    // If on jobalto.com without a locale path and IP check not done yet, wait
    // Skip for bots - they should see the content immediately without redirects
    if (!IS_SNAP && !IS_BOT && isOnJobalto && !hasLocaleInPath && !ipCountryChecked) {
      return <Preloader />; // Wait for IP-based redirect
    }

    // If on jobalto.com without a locale path (fallback if IP check failed).
    // Same-origin → stay in SPA.
    if (!IS_SNAP && !IS_BOT && isOnJobalto && !hasLocaleInPath && ipCountryChecked) {
      const fallbackLocale = 'en';
      const fallbackCountry = 'int';
      const pathSuffix = location.pathname === '/' ? '' : location.pathname;
      navigate(`/${fallbackLocale}-${fallbackCountry}${pathSuffix}`, { replace: true });
      return <Preloader />;
    }

    // Only redirect if we're on profitroll.com.ua (not already on jobalto.com)
    // and the locale should be on jobalto.com (fallback if IP check failed)
    // Exclude /language-selection from redirect as users need to access it to choose their language
    // Skip for bots - they should see the canonical content without redirects
    if (
      !IS_SNAP &&
      !IS_BOT &&
      !isOnJobalto &&
      ipCountryChecked &&
      location.pathname !== '/language-selection' &&
      !PROFITROLL_URL_LANGUAGES.includes(currentLocale) &&
      SUPPORTED_LOCALES.includes(currentLocale)
    ) {
      // Preserve the path when redirecting to jobalto.com
      const pathSuffix = location.pathname === '/' ? '' : location.pathname;
      window.location.href = `https://${JOBALTO_URL}/${currentLocale}-${currentCountry}${pathSuffix}`;
      return <Preloader />;
    }

    // Don't render routes if we're redirecting
    if (isRedirecting) {
      return <Preloader />;
    }
  }

  // Render child routes via Outlet - this ensures proper hydration matching
  return (
    <>
      <Outlet />
      <CookieConsentCreative />
    </>
  );
};

// Define routes as a reusable array to avoid duplication
const appRoutes = [
  { index: true, element: <Home /> },
  { path: 'sign-in', element: <SignInScreen /> },
  { path: 'sign-up', element: <SignUpScreen /> },
  // /dashboard is a role-resolver → /dashboard/user or /dashboard/counselor.
  { path: 'dashboard', element: <DashboardResolver /> },
  { path: 'dashboard/user', element: <Profile /> },
  { path: 'dashboard/counselor', element: <CounselorDashboard /> },
  { path: 'dashboard/admin', element: <AssessmentAdminDashboard /> },
  { path: 'dashboard/assessments', element: <AssessmentAdminDashboard /> },
  { path: 'profile', element: <ProfileRedirect /> },
  { path: 'forgot-password', element: <ResetPassword /> },
  // Firebase email-action handler (verifyEmail / resetPassword / recoverEmail).
  // Set as the custom action URL in the Firebase Console.
  { path: 'auth/action', element: <AuthAction /> },
  { path: 'professions', element: <Professions /> },
  { path: 'professions/all', element: <ProfessionsAll /> },
  { path: 'professions/:slug/:id', element: <Profession /> },
  { path: 'professions/:slug/:id/:slide', element: <Profession /> },
  { path: 'skills', element: <Skills /> },
  { path: 'skills/page/:num', element: <Skills /> },
  { path: 'skills/:slug/:id', element: <SkillDetail /> },
  { path: 'amp/:slug/:id', element: <Profession /> },
  { path: 'amp/:slug/:id/:slide', element: <Profession /> },
  { path: 'education', element: <EducationProgramsPage /> },
  { path: 'education/universities', element: <InstitutionsPage institutionType="university" /> },
  { path: 'education/universities/programs', element: <EducationPrograms institutionType="university" /> },
  { path: 'education/universities/:slug/:id', element: <InstitutionPage /> },
  { path: 'education/colleges', element: <InstitutionsPage institutionType="college" /> },
  { path: 'education/colleges/programs', element: <EducationPrograms institutionType="college" /> },
  { path: 'education/colleges/:slug/:id', element: <InstitutionPage /> },
  { path: 'education/programs/:slug/:id', element: <EducationProgram /> },
  { path: 'education/courses', element: <Courses /> },
  { path: 'education/:slug/:id', element: <UniversityPage /> },
  { path: 'company/:slug/:id', element: <Company /> },
  { path: 'articles', element: <Posts /> },
  { path: 'articles/:slug/:id', element: <Post /> },
  { path: 'counselors', element: <Consultants /> },
  { path: 'counselors/:slug/:id', element: <Consultant /> },
  // Legacy /consultants URLs → /counselors (section renamed May 2026).
  { path: 'consultants', element: <ConsultantsRedirect /> },
  { path: 'consultants/:slug/:id', element: <ConsultantsRedirect /> },
  { path: 'mentors', element: <Mentors /> },
  { path: 'mentors/:slug/:id', element: <Mentor /> },
  { path: 'orders', element: <Orders /> },
  { path: 'orders/:slug/:id', element: <Order /> },
  { path: 'about', element: <AboutPage /> },
  { path: 'about-us', element: <AboutPage extended={true} /> },
  { path: 'policy', element: <Policy /> },
  { path: 'policy/refund', element: <RefundPolicy /> },
  { path: 'programs', element: <Programs /> },
  { path: 'vacancies', element: <Vacancies /> },
  { path: 'vacancies/:slug/:id', element: <Vacancy /> },
  { path: 'policy/privacy', element: <PrivacyPolicy /> },
  { path: 'policy/cookie', element: <CookiePolicy /> },
  { path: 'subscription', element: <SubscribeType /> },
  // Chat retired: the backend proxied /chat to a `profitroll-chat` service
  // that is no longer deployed, and nothing linked here (last paid chat
  // session was 2025-07-24). Routes removed so /chat renders the 404 page
  // instead of a dead upstream. pages/Chat is kept for a future restore.
  { path: 'professions/page/:num', element: <Professions /> },
  { path: 'articles/page/:num', element: <Posts /> },
  { path: 'counselors/page/:num', element: <Consultants /> },
  { path: 'consultants/page/:num', element: <ConsultantsRedirect /> },
  { path: 'mentors/page/:num', element: <Mentors /> },
  { path: 'education/platforms', element: <Platforms /> },
  { path: 'education/platforms/courses', element: <Courses /> },
  { path: 'education/platforms/:slug/:id', element: <Platform /> },
  { path: 'education/courses/:slug/:id', element: <Course /> },
  { path: 'language-selection', element: <LocalizationsPage /> },
  { path: 'assessment', element: <AssessmentRoute /> },
  { path: 'assessment/code', element: <AssessmentRoute /> },
  { path: 'assessment/purchase', element: <AssessmentRoute /> },
  { path: 'assessment/sign-up-gate', element: <AssessmentRoute /> },
  { path: 'assessment/voucher-intro', element: <AssessmentRoute /> },
  { path: 'assessment/consent', element: <AssessmentRoute /> },
  { path: 'assessment/info', element: <AssessmentRoute /> },
  { path: 'assessment/info/:step', element: <AssessmentRoute /> },
  { path: 'assessment/section/:phase', element: <AssessmentRoute /> },
  { path: 'assessment/section/:phase/review-skipped', element: <AssessmentRoute /> },
  { path: 'assessment/section/:phase/q/:question', element: <AssessmentRoute /> },
  { path: 'assessment/results', element: <AssessmentResultsRoute /> },
  // Voucher redeem deep-link carried by the proVoucher email.
  { path: 'assessment/redeem/:token', element: <AssessmentRoute /> },
  { path: 'pro', element: <AssessmentLandingPage /> },
  // Consultant onboarding — one URL per step (role | consultant | hr).
  // /pro/start redirects to /pro/start/role inside ProStart.
  { path: 'pro/start', element: <ProStart /> },
  { path: 'pro/start/:step', element: <ProStart /> },
  // Counselor activity pages live under /pro/*; each page guards itself with
  // `isAuthenticated()`. The counselor dashboard home is /dashboard/counselor
  // (reached via the /dashboard role-resolver). Role grants at registration
  // via `?source=pro` (services/api X-Source header) or first pack purchase.
  { path: 'pro/buy', element: <CounselorBuy /> },
  { path: 'pro/buy/return', element: <CounselorBuyReturn /> },
  { path: 'pro/buy/return/:orderId', element: <CounselorBuyReturn /> },
  { path: 'pro/vouchers', element: <CounselorVouchers /> },
  { path: 'pro/results', element: <CounselorResults /> },
  { path: 'pro/results/:voucherId', element: <AssessmentResultsRoute /> },
  { path: 'pro/listing', element: <CounselorListing /> },
  // Legacy /counselor* URLs → /dashboard or /pro/* (restructured May 2026).
  { path: 'counselor', element: <CounselorRedirect /> },
  { path: 'counselor/profile', element: <CounselorRedirect /> },
  { path: 'counselor/vouchers', element: <CounselorRedirect /> },
  { path: 'counselor/buy', element: <CounselorRedirect /> },
  { path: 'counselor/buy/return', element: <CounselorRedirect /> },
  { path: 'counselor/buy/return/:orderId', element: <CounselorRedirect /> },
  { path: 'counselor/results', element: <CounselorRedirect /> },
  { path: 'counselor/listing', element: <CounselorRedirect /> },
  // Onboarding deep-link entry points. Every step has its own URL (e.g.
  // /onboarding/intro, /onboarding/name) so screens are bookmarkable and
  // the back button steps backwards through the flow. Step slugs are
  // defined in src/pages/Onboarding/index.jsx → STEP_SLUGS.
  // /onboarding (no slug) and /onboarding/start both render the landing.
  { path: 'onboarding', element: <OnboardingPage /> },
  { path: 'onboarding/:step', element: <OnboardingPage /> },
  { path: 'sandbox', element: <Sandbox /> },
  // Occupation simulators (prototype): hub + one page per occupation pack.
  { path: 'simulator', element: <SimulatorHub /> },
  { path: 'simulator/:simId', element: <SimulatorPlay /> },
  { path: 'simulator/:simId/:page', element: <SimulatorPlay /> },
  { path: '*', element: <Page404 /> },
];

// Render route elements from the routes array
const renderRoutes = () =>
  appRoutes.map((route, index) =>
    route.index ? (
      <Route key={index} index element={route.element} />
    ) : (
      <Route key={index} path={route.path} element={route.element} />
    )
  );

const RouterWrapper = () => (
  // No top-level Suspense fallback: in prod the prerendered HTML already
  // shows the page, and in dev the Home/Professions/etc. chunks load fast
  // enough that a brief blank beats a centred CircularProgress overlay.
  // The Preloader component remains in src/components/Preloader.jsx for
  // any opt-in use (e.g. payment-redirect screens).
  <Suspense fallback={null}>
    <Router>
      <ScrollResetOnNavigate />
      <SnackbarWrapper />
      <Routes>
        {/* 
          Both route patterns are rendered for consistent hydration.
          React Router ranks routes by specificity:
          - Static segments beat dynamic segments
          - The non-localized routes have static paths (e.g., /articles)
          - The localized route has a dynamic :lc param
          
          Non-localized routes should be checked first to ensure
          paths like /articles match the static 'articles' route,
          not the dynamic /:lc/* route with lc='articles'.
        */}
        
        {/* Non-localized routes (profitroll.com.ua) - has static path children */}
        <Route element={<LayoutDefaultLocale />}>
          {renderRoutes()}
        </Route>

        {/* Localized routes (jobalto.com) - matches paths with locale prefix */}
        <Route path="/:lc/*" element={<LayoutWithLocale />}>
          {renderRoutes()}
        </Route>
      </Routes>
    </Router>
  </Suspense>
);

export default RouterWrapper;
